import React from 'react';

import { Icon } from '@tecma/ds';
import { Trans, useTranslation } from '@tecma/i18n';
import { useSelector } from 'react-redux';

import { applicationStateSelectors } from 'store/slices/applicationState/selectors';

import currencyFormatter from 'utils/currencyFormatter';
import { isExtraSpaceCellar } from 'utils/extraSpaceType';

import type { ExtraSpacePriceDetails, ExtraSpaceType } from 'types/expenses';

import './extra-space-card.scss';

interface ExtraSpaceCardRequiredProps {
  id: string;
  floor: number;
  sub: number;
  price: number;
  type: ExtraSpaceType;
}

// Optional Props
interface ExtraSpaceCardOptionalProps {
  included?: boolean;
  pricing?: {
    monthlyPriceTotal: number;
    priceDetails: ExtraSpacePriceDetails[];
  };
}

// Combined required and optional props to build the full prop interface
export interface ExtraSpaceCardProps extends ExtraSpaceCardRequiredProps, ExtraSpaceCardOptionalProps {}

const ExtraSpaceCard: React.FC<ExtraSpaceCardProps> = ({ id, floor, sub, price, type, included, pricing }) => {
  const { t } = useTranslation();
  const area = useSelector(applicationStateSelectors.selectArea);

  return (
    <div className='extra-space-card'>
      <div className='extra-space-card-header'>
        <div className='name'>
          {isExtraSpaceCellar(type) ? (
            <>
              <Icon size='small' iconName='wine' filled />
              {t('extra-space.cellar')}
            </>
          ) : (
            <>
              <Icon size='small' iconName='garage' filled />
              {t('extra-space.box')}
            </>
          )}
        </div>
        <div className='price'>
          {included ? (
            <span className='included'>
              <strong>{t('quote.included')}</strong>
            </span>
          ) : (
            <>
              <Trans
                i18nKey='quote.price'
                values={{
                  price: currencyFormatter('EUR', pricing?.monthlyPriceTotal ?? price),
                }}
                context={area === 'rent' ? area : undefined}
                components={{ span: <span /> }}
              />
              {area === 'sale' && <span className='note-number'>1</span>}
            </>
          )}
        </div>
      </div>
      {!!(floor || id || sub) && (
        <div className='extra-space-card-footer'>
          {!!floor && (
            <div className='feature'>
              <span className='label'>{t('extra-space.floor')}</span>
              <span className='value'>{floor}</span>
            </div>
          )}
          {id && (
            <div className='feature'>
              <span className='label'>{t('extra-space.id')}</span>
              <span className='value'>{id}</span>
            </div>
          )}
          {!!sub && (
            <div className='feature'>
              <span className='label'>{t('extra-space.sub')}</span>
              <span className='value'>{sub}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(ExtraSpaceCard);
